import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { BOOKING_URL, CONTACT, STUDIO } from "../data/content";
import { useT } from "../i18n/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useT();

  const LINKS = [
    { href: "/#leistungen", label: t("nav.beauty"), key: "beauty", type: "anchor" },
    { href: "/friseur", label: t("nav.hair"), key: "hair", type: "route" },
    { href: "/#ueber-uns", label: t("nav.about"), key: "about", type: "anchor" },
    { href: "/#kontakt", label: t("nav.contact"), key: "contact", type: "anchor" },
  ];

  const isHair = location.pathname === "/friseur";
  const ctaHref = isHair ? `tel:${CONTACT.hairPhoneLink}` : BOOKING_URL;
  const ctaTargetProps = isHair
    ? {}
    : { target: "_blank", rel: "noopener noreferrer" };
  const ctaLabel = isHair ? t("nav.book_phone") : t("nav.book");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <span className="text-[#D4AF37] font-serif-display text-2xl tracking-wide">N</span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif-display text-lg text-white tracking-wide">{STUDIO.name}</span>
            <span className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]/80">Frankfurt</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => {
            const testid = `nav-link-${l.key}`;
            const cls =
              "text-[11px] tracking-[0.28em] uppercase text-white/70 hover:text-[#D4AF37] transition-colors duration-300";
            if (l.type === "route") {
              return (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={testid}
                  className={`${cls} ${location.pathname === l.href ? "text-[#D4AF37]" : ""}`}
                >
                  {l.label}
                </Link>
              );
            }
            return (
              <a key={l.href} href={l.href} data-testid={testid} className={cls}>
                {l.label}
              </a>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <LanguageSwitcher />
          <a
            href={ctaHref}
            {...ctaTargetProps}
            data-testid="nav-book-btn"
            className="inline-flex btn-gold"
          >
            {ctaLabel}
          </a>
        </div>

        <button
          aria-label={t("nav.menu")}
          data-testid="mobile-menu-btn"
          className="lg:hidden text-white/90"
          onClick={() => setOpen((s) => !s)}
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) => {
                const testid = `mobile-nav-link-${l.key}`;
                const cls =
                  "text-base tracking-[0.22em] uppercase text-white/85 hover:text-[#D4AF37] transition-colors";
                if (l.type === "route") {
                  return (
                    <Link
                      key={l.href}
                      to={l.href}
                      onClick={() => setOpen(false)}
                      data-testid={testid}
                      className={cls}
                    >
                      {l.label}
                    </Link>
                  );
                }
                return (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={testid}
                    className={cls}
                  >
                    {l.label}
                  </a>
                );
              })}
              <div className="pt-2">
                <LanguageSwitcher variant="mobile" />
              </div>
              <a
                href={ctaHref}
                {...ctaTargetProps}
                data-testid="mobile-book-btn"
                className="btn-gold w-full mt-2"
                onClick={() => setOpen(false)}
              >
                {ctaLabel}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
